import {
  LeaderCollaborationLevel,
  LeaderInsightsTaskStatus,
  LeaderScoreZone,
  type LeaderHighlight,
  type LeaderInsightsTask,
  type LeaderScoreBucket,
  type LeaderScoreSpread
} from '../types/Leader'
import { getArray, toRangeIndex } from './Common'
import { ONE_SECOND } from './Consts'

// Constants and pure maths for the leader "My people" analysis, shared by the builder and the page.

export const LEADER_INSIGHTS_MIN_PEOPLE = 3 // people with results before group numbers are shown
export const LEADER_INSIGHTS_SPREAD_MIN_PEOPLE = 3 // people with results before the lowest, highest and count per range
export const LEADER_PERSONALITY_MIN_PEOPLE = 2 // people with a personality test before the group's profile is shown
export const LEADER_SCOPE_MAX_USERS = 200 // one Firestore document, one generation run
export const LEADER_SCORE_BUCKET_COUNT = 5 // 0–0.2, 0.2–0.4, … 0.8–1.0
export const LEADER_INSIGHTS_TASK_MAX_ATTEMPTS = 3
export const LEADER_INSIGHTS_POLL_INTERVAL_MS = 5 * ONE_SECOND // 5 s, while a regeneration is running
export const LEADER_INSIGHTS_REFETCH_INTERVAL_MS = 90 * ONE_SECOND // 90 s, while the page is open
/** The shape results are built in; results in an older one are rebuilt when they are next read. */
export const LEADER_INSIGHTS_FORMAT_VERSION = 5 // 5: every behaviour carries its level; no survey answer among a team's

/** The collaboration levels from the highest down, matching the floors in order; below the last floor is very low. */
const COLLABORATION_LEVELS_FROM_HIGHEST = [
  LeaderCollaborationLevel.VERY_HIGH,
  LeaderCollaborationLevel.HIGH,
  LeaderCollaborationLevel.ABOVE_HALF,
  LeaderCollaborationLevel.BELOW_HALF,
  LeaderCollaborationLevel.LOW
]

/** Where a 0–1 result turns middle and high. */
export type LeaderScoreZoneFloors = { middleFrom: number; highFrom: number }

/** The numbers a leader's results are graded with (Data Customization values of the `leaderInsights` area). */
export type LeaderInsightsValues = {
  scoreZoneFloors: LeaderScoreZoneFloors
  questionZoneFloors: LeaderScoreZoneFloors
  collaborationLevelFloors: number[]
  topMetricsCount: number
}

const SCORE_DECIMALS_FACTOR = 100 // two decimals

/** One scope, one insights document and one task per leader per organization. */
export const buildLeaderDocId = (organizationId: string, leaderUserId: string): string =>
  `${organizationId}_${leaderUserId}`

/** A run is known by when it was claimed, so a task claimed again since is another run. */
export const isSameLeaderInsightsRun = (stored: LeaderInsightsTask, claimed: LeaderInsightsTask): boolean =>
  stored.status === LeaderInsightsTaskStatus.RUNNING && stored.startedAt === claimed.startedAt

/** Aria's note as one paragraph, for where it is read back rather than shown: her points, then her suggestion. */
export const joinLeadershipNote = (highlights: LeaderHighlight[], suggestion?: string): string =>
  [...highlights.map((highlight) => highlight.detail), suggestion ?? ''].filter(Boolean).join(' ')

export const roundScore = (score: number): number => Math.round(score * SCORE_DECIMALS_FACTOR) / SCORE_DECIMALS_FACTOR

export function resolveScoreZone(score: number, floors: LeaderScoreZoneFloors): LeaderScoreZone {
  if (score >= floors.highFrom) return LeaderScoreZone.HIGH
  if (score >= floors.middleFrom) return LeaderScoreZone.MIDDLE

  return LeaderScoreZone.LOW
}

/** The level of a 0–1 share: how many of the people do a behaviour well, or a team's own result on the same scale. */
export function resolveCollaborationLevel(share: number, floors: number[]): LeaderCollaborationLevel {
  const levelIndex = floors.findIndex((floor) => share >= floor)
  return COLLABORATION_LEVELS_FROM_HIGHEST[levelIndex] ?? LeaderCollaborationLevel.VERY_LOW
}

// A score of exactly 1 belongs to the last bucket rather than to one past the end.
export function bucketScores(scores: number[], bucketCount: number): LeaderScoreBucket[] {
  const bucketWidth = 1 / bucketCount
  const buckets: LeaderScoreBucket[] = getArray(bucketCount).map((bucketIndex) => ({
    fromInclusive: roundScore(bucketIndex * bucketWidth),
    toExclusive: roundScore((bucketIndex + 1) * bucketWidth),
    count: 0
  }))

  scores.forEach((score) => {
    buckets[toRangeIndex(score, bucketCount)].count += 1
  })

  return buckets
}

/** The two ends and the count per range; nothing for a group below the minimum for them. */
export function buildScoreSpread(scores: number[]): LeaderScoreSpread | undefined {
  const isLargeEnough = scores.length >= LEADER_INSIGHTS_SPREAD_MIN_PEOPLE
  if (!isLargeEnough) return undefined

  return {
    min: Math.min(...scores),
    max: Math.max(...scores),
    buckets: bucketScores(scores, LEADER_SCORE_BUCKET_COUNT)
  }
}
